import { useState, useEffect, useCallback, useMemo } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { I18nProvider, useI18n } from "./i18n";
import { Toaster } from "sonner";
import { LayoutDashboard, Target, AlertTriangle, Terminal, FileText, Settings as SettingsIcon, Menu, X, Shield, Key, Users, ShieldCheck, Cloud, Monitor } from "lucide-react";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ServicesStatus from "./components/ServicesStatus";
import NotificationCenter from "./components/NotificationCenter";
import UserMenu from "./components/UserMenu";
import WorkspaceSidebar from "./components/WorkspaceSidebar";
import WorkspaceModal from "./components/WorkspaceModal";
import WorkspaceGuard from "./components/WorkspaceGuard";
import { AIAssistantButton } from "./components/AIAssistant";
import { WorkspaceProvider, useWorkspace } from "./contexts/WorkspaceContext";
import { ReportGenerationProvider } from "./contexts/ReportGenerationContext";
import useActivityTracker from "./hooks/useActivityTracker";
import Login from "./pages/Login/Login";
import Register from "./pages/Register/Register";
import VerifyEmail from "./pages/VerifyEmail/VerifyEmail";
import VerifyLogin from "./pages/Security/VerifyLogin";
import SetupWizard from "./pages/SetupWizard/SetupWizard";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import PricingPage from "./pages/Pricing/PricingPage";
import Dashboard from "./pages/Dashboard/Dashboard";
import ScansList from "./pages/Scans/ScansList";
import ScanDetails from "./pages/Scans/ScanDetails";
import IoTScanDetails from "./pages/Scans/IoTScanDetails";
import NewScan from "./pages/Scans/NewScan";
import VulnerabilitiesList from "./pages/Vulnerabilities/VulnerabilitiesList";
import VulnerabilityDetails from "./pages/Vulnerabilities/VulnerabilityDetails";
import SessionsList from "./pages/Sessions/SessionsList";
import SessionDetails from "./pages/Sessions/SessionDetails";
import APTModulesDashboard from "./pages/Sessions/APTModulesDashboard";
import EquipmentDashboard from "./pages/Equipment/EquipmentDashboard";
import { EnrichmentDashboard } from "./pages/Enrichment";
import SubdomainDiscovery from "./pages/Subdomains/SubdomainDiscovery";
import CredentialsList from "./pages/Credentials/CredentialsList";
import ReportsList from "./pages/Reports/ReportsList";
import GeneratedReportsList from "./pages/Reports/GeneratedReportsList";
import GeneratedReportDetail from "./pages/Reports/GeneratedReportDetail";
import Settings from "./pages/Settings/Settings";
import ADPentest from "./pages/AD/ADPentest";
import ADSessionDetail from "./pages/AD/ADSessionDetail";
import PingCastleDetail from "./pages/AD/PingCastleDetail";
import NetworkMap from "./pages/NetworkMap/NetworkMap";
import NotificationsList from "./pages/Notifications/NotificationsList";
import MITRENavigatorPage from "./pages/MITRENavigator/MITRENavigatorPage";
import AttackPage from "./pages/Attack/AttackPage";
import WorkspaceManager from "./pages/Workspaces/WorkspaceManager";
import WorkspaceDetails from "./pages/Workspaces/WorkspaceDetails";
import Account from "./pages/Account/Account";
import ErrorPage from "./pages/Error/ErrorPage";
import TicketsList from "./pages/Support/TicketsList";
import NewTicket from "./pages/Support/NewTicket";
import TicketDetails from "./pages/Support/TicketDetails";
import ReportTypes from "./pages/Reports/ReportTypes";
import CreditsPage from "./pages/Credits/CreditsPage";
import ContractSignPage from "./pages/Contracts/ContractSignPage";
import MagicLogin from "./pages/MagicLogin/MagicLogin";
import AuthRedirect from "./pages/AuthRedirect/AuthRedirect";
import AcceptInvitation from "./pages/AcceptInvitation/AcceptInvitation";
import SharePage from "./pages/Share/SharePage";
import { AntivirusPage } from "./pages/Antivirus";
import { PhishingPage } from "./pages/Phishing";
import DedicatedServer from "./pages/DedicatedServer/DedicatedServer";
import ContactPage from "./pages/Contact/ContactPage";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import LicenseExpiredPage from "./pages/License/LicenseExpiredPage";
import LicenseGuard from "./components/LicenseGuard";
import OrchestratorMonitoring from "./pages/Orchestrator/OrchestratorMonitoring";
import AgentsList from "./pages/Agents/AgentsList";
import AgentDetails from "./pages/Agents/AgentDetails";
import LLMMonitor from "./pages/LLM/LLMMonitor";
import { auth } from "./api/client";
import { Crosshair, Building2 } from "lucide-react";
import { useParams } from "react-router-dom";

// Invitation Redirect Component - redirects /invitation/:token to /accept-invitation?token=:token
const InvitationRedirect = () => {
  const { token } = useParams();
  return <Navigate to={`/accept-invitation?token=${token}`} replace />;
};

// Protected Route Component with token validation
const ProtectedRoute = ({ children }) => {
  const { t } = useI18n();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const hasToken = auth.isAuthenticated();
  // Check if token is valid client-side (not expired) before making API calls
  const isTokenValidClientSide = auth.isTokenValidClientSide();

  useEffect(() => {
    let isMounted = true;

    const validateToken = async () => {
      // No token at all - skip API call
      if (!hasToken) {
        if (isMounted) {
          setIsValidating(false);
          setIsValid(false);
        }
        return;
      }

      // Token exists but is expired client-side - skip API call to avoid 401
      if (!isTokenValidClientSide) {
        if (isMounted) {
          setIsValidating(false);
          setIsValid(false);
        }
        return;
      }

      try {
        // Validate token by calling /auth/me endpoint
        // Only called for tokens that pass client-side validation
        await auth.getCurrentUser();
        if (isMounted) {
          setIsValid(true);
          setIsValidating(false);
        }
      } catch (error) {
        // If 401, the API interceptor will handle redirect to login
        // For other errors, also redirect to login for safety
        if (isMounted) {
          setIsValid(false);
          setIsValidating(false);
          // Clear invalid token and redirect
          if (error.response?.status !== 401) {
            localStorage.removeItem('access_token');
          }
        }
      }
    };

    validateToken();

    return () => {
      isMounted = false;
    };
  }, [hasToken, isTokenValidClientSide]);

  // No token at all - redirect immediately
  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  // Validating token - show loading spinner
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green mx-auto mb-4"></div>
          <p className="text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Token invalid - the interceptor should redirect, but handle edge cases
  if (!isValid) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex min-h-screen">
      <WorkspaceSidebar />
      <div className="flex-1 flex flex-col min-w-0 lg:ml-[68px]">
        {children}
      </div>
      {/* AI Assistant Button */}
      <AIAssistantButton />
    </div>
  );
};

// SuperAdmin Route Component - only superadmin can access
const SuperAdminRoute = ({ children }) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const isAuthenticated = auth.isAuthenticated();
  // Check if token is valid client-side (not expired) before making API calls
  const isTokenValidClientSide = auth.isTokenValidClientSide();

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      // No token at all - skip API call
      if (!isAuthenticated) {
        if (isMounted) setIsLoading(false);
        return;
      }

      // Token exists but is expired client-side - skip API call to avoid 401
      if (!isTokenValidClientSide) {
        if (isMounted) setIsLoading(false);
        return;
      }

      try {
        // Add timeout to prevent stuck loading state
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 10000)
        );
        const userData = await Promise.race([auth.getCurrentUser(), timeoutPromise]);
        const role = userData?.role || '';

        // Only superadmin has access
        if (isMounted) {
          setHasAccess(role === 'super_admin' || userData?.is_superuser);
        }
      } catch (error) {
        // Silently handle 401 (user will be redirected to login by API client)
        if (error.response?.status !== 401) {
          console.error('Error checking user role:', error);
        }
        if (isMounted) setHasAccess(false);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isTokenValidClientSide]);

  // No token at all or token is expired - redirect to login
  if (!isAuthenticated || !isTokenValidClientSide) {
    return <Navigate to="/login" replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green mx-auto mb-4"></div>
          <p className="text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex min-h-screen">
      <WorkspaceSidebar />
      <div className="flex-1 flex flex-col min-w-0 lg:ml-[68px]">
        {children}
      </div>
    </div>
  );
};

// ============================================
// CYBER BACKGROUND ANIMATION COMPONENT
// ============================================
const CyberBackground = () => {
  const matrixChars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';

  // Generate matrix columns
  const columns = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: `${(i / 30) * 100}%`,
      delay: Math.random() * 10,
      duration: 15 + Math.random() * 20,
      chars: Array.from({ length: 25 }, () => matrixChars[Math.floor(Math.random() * matrixChars.length)]).join('\n')
    }));
  }, []);

  // Generate floating nodes
  const nodes = useMemo(() => {
    return Array.from({ length: 15 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      endX: `${(Math.random() - 0.5) * 200}px`,
      endY: `${(Math.random() - 0.5) * 200}px`,
      delay: Math.random() * 5,
      duration: 10 + Math.random() * 10
    }));
  }, []);

  return (
    <div className="cyber-background">
      {/* Grid pattern */}
      <div className="cyber-grid" />

      {/* Scanning line */}
      <div className="cyber-scanline" />
      <div className="cyber-scanline" style={{ animationDelay: '2s' }} />

      {/* Matrix columns */}
      <div className="cyber-matrix">
        {columns.map(col => (
          <div
            key={col.id}
            className="cyber-matrix-column"
            style={{
              left: col.left,
              animationDelay: `${col.delay}s`,
              animationDuration: `${col.duration}s`
            }}
          >
            {col.chars}
          </div>
        ))}
      </div>

      {/* Floating nodes */}
      {nodes.map(node => (
        <div
          key={node.id}
          className="cyber-node"
          style={{
            left: node.left,
            top: node.top,
            '--end-x': node.endX,
            '--end-y': node.endY,
            animationDelay: `${node.delay}s`,
            animationDuration: `${node.duration}s`
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// MOUSE FOLLOWER COMPONENT
// ============================================
const MouseFollower = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setPosition({ x: e.clientX, y: e.clientY });
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseOver = (e) => {
      const target = e.target;
      if (target.tagName === 'A' || target.tagName === 'BUTTON' ||
          target.closest('a') || target.closest('button') ||
          target.classList.contains('clickable')) {
        setIsHovering(true);
      }
    };

    const handleMouseOut = (e) => {
      const target = e.target;
      if (target.tagName === 'A' || target.tagName === 'BUTTON' ||
          target.closest('a') || target.closest('button') ||
          target.classList.contains('clickable')) {
        setIsHovering(false);
      }
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      <div
        className="mouse-follower"
        style={{
          left: position.x,
          top: position.y,
          opacity: isVisible ? 1 : 0
        }}
      />
      <div
        className={`mouse-follower-inner ${isHovering ? 'hovering' : ''}`}
        style={{
          left: position.x,
          top: position.y
        }}
      />
    </>
  );
};

// Activity Tracker Wrapper - placed inside BrowserRouter
const ActivityTrackerWrapper = ({ children }) => {
  useActivityTracker();
  return children;
};

// Workspace Modal Wrapper - shows modal when workspace selection is needed
const WorkspaceModalWrapper = () => {
  const { showSelector, setShowSelector, hasWorkspace } = useWorkspace();
  const location = useLocation();

  // Public routes where the workspace modal should never appear
  const publicRoutes = ['/login', '/register', '/verify-email', '/setup', '/reset-password',
    '/magic-login', '/accept-invitation', '/auth-redirect', '/security/verify-login'];
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  // Only show for authenticated users on protected routes
  if (isPublicRoute || !auth.isAuthenticated()) return null;

  return (
    <WorkspaceModal
      show={showSelector}
      onClose={() => setShowSelector(false)}
      allowClose={hasWorkspace}
    />
  );
};

const Header = () => {
  const { t } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const location = useLocation();

  // Public routes where we should not fetch user data (avoids 401 errors)
  const publicRoutes = ['/login', '/register', '/verify-email', '/setup', '/reset-password',
    '/magic-login', '/accept-invitation', '/auth-redirect', '/security/verify-login',
    '/dedicated-server', '/share', '/sign', '/pricing'];
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  // Fetch user plan and role on mount
  useEffect(() => {
    const fetchUserData = async () => {
      // Skip API calls on public routes to avoid 401 errors
      if (isPublicRoute) {
        return;
      }

      try {
        // Check both token existence AND validity (not expired)
        if (auth.isAuthenticated() && auth.isTokenValidClientSide()) {
          const userData = await auth.getCurrentUser();
          setUserPlan(userData?.subscription_plan || 'free');
          setUserRole(userData?.role || '');
          setIsSuperuser(userData?.is_superuser || false);
        }
      } catch (error) {
        // Silently handle 401 (user will be redirected to login by API client)
        if (error.response?.status !== 401) {
          console.error('Error fetching user data:', error);
        }
        setUserPlan('free');
        setUserRole('');
        setIsSuperuser(false);
      }
    };
    fetchUserData();
  }, [isPublicRoute]);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  // Check if user can access premium features (superadmin, "groupe" or "dedicated" plans)
  const isSuperAdmin = userRole === 'super_admin' || isSuperuser || userPlan === 'superadmin';
  const canAccessPremiumFeatures = isSuperAdmin || userPlan === 'groupe' || userPlan === 'dedicated' || userPlan === 'sur_mesure';

  return (
    <header className="liquid-glass sticky top-0 z-50 border-b border-brand-green/20">
      <div className="w-full px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Logo - fixed width */}
          <Link to="/" className="hover:opacity-80 transition-opacity flex-shrink-0 ml-0 lg:ml-[68px]">
            <img
              src="/logo.png"
              alt="SYLINK Logo"
              className="h-8 sm:h-10 w-auto object-contain max-w-[120px] sm:max-w-[160px]"
            />
          </Link>

          {/* Nav - takes remaining space, hidden on small screens */}
          <nav className="hidden lg:flex items-center justify-center flex-1 gap-1 xl:gap-3 2xl:gap-5 overflow-hidden min-w-0">
            <NavLink to="/" icon={<LayoutDashboard className="w-4 h-4" />} active={isActive('/')}>{t('nav.dashboard')}</NavLink>
            <NavLink to="/scans" icon={<Target className="w-4 h-4" />} active={isActive('/scans')}>{t('nav.scans')}</NavLink>
            <NavLink to="/ad" icon={<Users className="w-4 h-4" />} active={isActive('/ad')}>{t('nav.ad')}</NavLink>
            <NavLink to="/credentials" icon={<Key className="w-4 h-4" />} active={isActive('/credentials')}>{t('nav.credentials')}</NavLink>
            {/* Vulnerabilities - Hidden temporarily
            <NavLink to="/vulnerabilities" icon={<AlertTriangle className="w-4 h-4" />} active={isActive('/vulnerabilities')}>{t('nav.vulnerabilities')}</NavLink>
            */}
            {/* Sessions - Hidden temporarily
            <NavLink to="/sessions" icon={<Terminal className="w-4 h-4" />} active={isActive('/sessions')}>{t('nav.sessions')}</NavLink>
            */}
            {/* Attack! - Hidden temporarily
            <NavLink to="/attack" icon={<Crosshair className="w-4 h-4" />} active={isActive('/attack')} isAttack={true}>Attack!</NavLink>
            */}
            <NavLink to="/reports" icon={<FileText className="w-4 h-4" />} active={isActive('/reports')}>{t('nav.reports')}</NavLink>
            <NavLink to="/workspaces" icon={<Building2 className="w-4 h-4" />} active={isActive('/workspaces')}>{t('nav.workspaces')}</NavLink>
            {isSuperAdmin && (
              <NavLink to="/settings" icon={<SettingsIcon className="w-4 h-4" />} active={isActive('/settings')}>{t('nav.settings')}</NavLink>
            )}
          </nav>

          {/* Right side - user controls, fixed width */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-auto">
            <NotificationCenter />
            <UserMenu />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-brand-green p-2 rounded-lg liquid-glass-hover flex-shrink-0"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 space-y-2 border-t border-brand-green/20 pt-4">
            <MobileNavLink to="/" icon={<LayoutDashboard className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/')}>{t('nav.dashboard')}</MobileNavLink>
            <MobileNavLink to="/scans" icon={<Target className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/scans')}>{t('nav.scans')}</MobileNavLink>
            <MobileNavLink to="/ad" icon={<Users className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/ad')}>{t('nav.ad')}</MobileNavLink>
            <MobileNavLink to="/credentials" icon={<Key className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/credentials')}>{t('nav.credentials')}</MobileNavLink>
            {/* Vulnerabilities - Hidden temporarily
            <MobileNavLink to="/vulnerabilities" icon={<AlertTriangle className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/vulnerabilities')}>{t('nav.vulnerabilities')}</MobileNavLink>
            */}
            {/* Sessions - Hidden temporarily
            <MobileNavLink to="/sessions" icon={<Terminal className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/sessions')}>{t('nav.sessions')}</MobileNavLink>
            */}
            {/* Attack! - Hidden temporarily
            <MobileNavLink to="/attack" icon={<Crosshair className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/attack')} isAttack={true}>Attack!</MobileNavLink>
            */}
            <MobileNavLink to="/reports" icon={<FileText className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/reports')}>{t('nav.reports')}</MobileNavLink>
            <MobileNavLink to="/workspaces" icon={<Building2 className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/workspaces')}>{t('nav.workspaces')}</MobileNavLink>
            {isSuperAdmin && (
              <MobileNavLink to="/settings" icon={<SettingsIcon className="w-5 h-5" />} onClick={() => setIsMenuOpen(false)} active={isActive('/settings')}>{t('nav.settings')}</MobileNavLink>
            )}
          </nav>
        )}
      </div>
    </header>
  );
};

const NavLink = ({ to, icon, children, active, isAttack, hideTextOnMedium = true }) => {
  return (
    <Link
      to={to}
      title={typeof children === 'string' ? children : undefined}
      className={`nav-liquid-link flex items-center gap-1.5 xl:gap-2 font-medium whitespace-nowrap ${
        isAttack
          ? active
            ? 'active text-red-500'
            : 'text-red-400 hover:text-red-500'
          : active
            ? 'active text-brand-green'
            : 'text-gray-400 hover:text-brand-green'
      }`}
      style={isAttack ? { textShadow: active ? '0 0 10px rgba(239, 68, 68, 0.5)' : 'none' } : {}}
    >
      <span className="flex-shrink-0">{icon}</span>
      <span className={hideTextOnMedium ? 'hidden xl:inline text-sm' : 'text-sm'}>{children}</span>
    </Link>
  );
};

const MobileNavLink = ({ to, icon, children, onClick, active, isAttack }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-lg liquid-glass-hover ${
        isAttack
          ? active
            ? 'text-red-500 liquid-glass'
            : 'text-red-400 hover:text-red-500'
          : active
            ? 'text-brand-green liquid-glass'
            : 'text-gray-400 hover:text-brand-green'
      }`}
    >
      {icon}
      <span>{children}</span>
    </Link>
  );
};

const Footer = () => {
  const { t } = useI18n();
  const [licenseInfo, setLicenseInfo] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { userSettings } = await import('./services/api/client');
        const settings = await userSettings.get();
        const lic = settings?.license_settings;
        if (lic?.license_id) {
          let daysRemaining = null;
          if (lic.expires_at) {
            const diff = new Date(lic.expires_at) - new Date();
            daysRemaining = Math.max(0, Math.ceil(diff / 86400000));
          }
          setLicenseInfo({ id: lic.license_id, daysRemaining });
        }
      } catch { /* ignore */ }
    };
    load();
  }, []);

  return (
    <footer className="liquid-glass border-t border-brand-green/20 mt-12 overflow-hidden">
      <div className="container mx-auto px-6 lg:pl-[68px] py-6 relative z-10 overflow-hidden">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left min-w-0 flex-1">
            <p className="text-white font-semibold truncate">{t('footer.title')}</p>
            <p className="text-sm text-gray-400 truncate">{t('footer.subtitle')}</p>
          </div>
          <div className="flex flex-col items-center md:items-end gap-3 min-w-0 flex-shrink-0 max-w-full md:max-w-[60%]">
            <ServicesStatus />
            <div className="flex flex-wrap justify-center md:justify-end items-center gap-4 max-w-full overflow-hidden">
              <p className="text-sm text-gray-400 whitespace-nowrap">{t('footer.version')} <span className="text-brand-green font-mono">3.0.0</span></p>
              {licenseInfo && (
                <p className="text-sm text-gray-400 whitespace-nowrap">
                  ID : <span className="text-brand-green font-mono">{licenseInfo.id}</span>
                </p>
              )}
              {licenseInfo?.daysRemaining !== null && licenseInfo?.daysRemaining !== undefined && (
                <p className="text-sm text-gray-400 whitespace-nowrap">
                  Licence : <span className={`font-mono font-semibold ${licenseInfo.daysRemaining <= 30 ? 'text-red-400' : licenseInfo.daysRemaining <= 90 ? 'text-yellow-400' : 'text-brand-green'}`}>
                    {licenseInfo.daysRemaining} jour{licenseInfo.daysRemaining > 1 ? 's' : ''} restant{licenseInfo.daysRemaining > 1 ? 's' : ''}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <I18nProvider>
    <div className="App bg-brand-black min-h-screen flex flex-col relative">
      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        richColors
        closeButton
        theme="dark"
      />

      {/* Cyber Attack Background Animation */}
      <CyberBackground />

      {/* Mouse Follower Effect */}
      <MouseFollower />

      {/* Main Content */}
      <div className="relative z-10 flex flex-col min-h-screen">
        <BrowserRouter>
          <WorkspaceProvider>
          <ReportGenerationProvider>
          <ScrollToTop />
          <WorkspaceModalWrapper />
          <WorkspaceGuard />
          <LicenseGuard />
          <ActivityTrackerWrapper>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<ErrorBoundary><Login /></ErrorBoundary>} />
            {/* Register route - only enabled if REACT_APP_DISABLE_REGISTRATION is not true */}
            {process.env.REACT_APP_DISABLE_REGISTRATION !== 'true' && (
              <Route path="/register" element={<ErrorBoundary><Register /></ErrorBoundary>} />
            )}
            <Route path="/verify-email" element={<ErrorBoundary><VerifyEmail /></ErrorBoundary>} />
            <Route path="/security/verify-login" element={<ErrorBoundary><VerifyLogin /></ErrorBoundary>} />
            <Route path="/setup" element={<SetupWizard />} />
            <Route path="/reset-password" element={<ErrorBoundary><ResetPassword /></ErrorBoundary>} />
            <Route path="/magic-login" element={<ErrorBoundary><MagicLogin /></ErrorBoundary>} />
            <Route path="/accept-invitation" element={<ErrorBoundary><AcceptInvitation /></ErrorBoundary>} />
            <Route path="/invitation/:token" element={<InvitationRedirect />} />
            <Route path="/auth-redirect" element={<AuthRedirect />} />
            <Route path="/share/:token" element={<SharePage />} />
            <Route path="/sign/:token" element={<ContractSignPage />} />
            <Route path="/dedicated-server" element={<><Header /><DedicatedServer /><Footer /></>} />

            {/* Protected Routes - Require authentication */}
            <Route path="/" element={<ProtectedRoute><Header /><Dashboard /><Footer /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Header /><Dashboard /><Footer /></ProtectedRoute>} />
            <Route path="/scans" element={<ProtectedRoute><Header /><ErrorBoundary><ScansList /></ErrorBoundary><Footer /></ProtectedRoute>} />
            <Route path="/scans/history" element={<Navigate to="/scans" replace />} />
            <Route path="/scans/scheduled" element={<Navigate to="/scans" replace />} />
            <Route path="/scans/new" element={<ProtectedRoute><Header /><ErrorBoundary><NewScan /></ErrorBoundary><Footer /></ProtectedRoute>} />
            <Route path="/scans/:id" element={<ProtectedRoute><Header /><ErrorBoundary><ScanDetails /></ErrorBoundary><Footer /></ProtectedRoute>} />
            <Route path="/scans/iot/:id" element={<ProtectedRoute><Header /><ErrorBoundary><IoTScanDetails /></ErrorBoundary><Footer /></ProtectedRoute>} />
            <Route path="/vulnerabilities" element={<ProtectedRoute><Header /><VulnerabilitiesList /><Footer /></ProtectedRoute>} />
            <Route path="/vulnerabilities/:id" element={<ProtectedRoute><Header /><ErrorBoundary><VulnerabilityDetails /></ErrorBoundary><Footer /></ProtectedRoute>} />
            <Route path="/credentials" element={<ProtectedRoute><Header /><CredentialsList /><Footer /></ProtectedRoute>} />
            <Route path="/sessions" element={<ProtectedRoute><Header /><ErrorBoundary><SessionsList /></ErrorBoundary><Footer /></ProtectedRoute>} />
            <Route path="/sessions/apt" element={<ProtectedRoute><Header /><ErrorBoundary><APTModulesDashboard /></ErrorBoundary><Footer /></ProtectedRoute>} />
            <Route path="/sessions/active" element={<Navigate to="/sessions" replace />} />
            <Route path="/sessions/:id" element={<ProtectedRoute><Header /><ErrorBoundary><SessionDetails /></ErrorBoundary><Footer /></ProtectedRoute>} />
            <Route path="/equipment" element={<ProtectedRoute><Header /><EquipmentDashboard /><Footer /></ProtectedRoute>} />
            <Route path="/enrichment" element={<ProtectedRoute><Header /><EnrichmentDashboard /><Footer /></ProtectedRoute>} />
            <Route path="/subdomains" element={<ProtectedRoute><Header /><SubdomainDiscovery /><Footer /></ProtectedRoute>} />
            <Route path="/antivirus" element={<ProtectedRoute><Header /><AntivirusPage /><Footer /></ProtectedRoute>} />
            <Route path="/phishing" element={<ProtectedRoute><Header /><PhishingPage /><Footer /></ProtectedRoute>} />
            <Route path="/ad" element={<ProtectedRoute><Header /><ADPentest /><Footer /></ProtectedRoute>} />
            <Route path="/ad/session/:sessionId" element={<ProtectedRoute><Header /><ADSessionDetail /><Footer /></ProtectedRoute>} />
            <Route path="/ad/pingcastle/:scanId" element={<ProtectedRoute><Header /><PingCastleDetail /><Footer /></ProtectedRoute>} />
            <Route path="/network-map" element={<ProtectedRoute><Header /><NetworkMap /><Footer /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Header /><ErrorBoundary><ReportsList /></ErrorBoundary><Footer /></ProtectedRoute>} />
            <Route path="/reports/templates" element={<Navigate to="/reports/types" replace />} />
            <Route path="/reports/types" element={<ProtectedRoute><Header /><ReportTypes /><Footer /></ProtectedRoute>} />
            <Route path="/reports/generated" element={<ProtectedRoute><Header /><GeneratedReportsList /><Footer /></ProtectedRoute>} />
            <Route path="/reports/generated/:id" element={<ProtectedRoute><Header /><GeneratedReportDetail /><Footer /></ProtectedRoute>} />
            <Route path="/settings" element={<SuperAdminRoute><Header /><Settings /><Footer /></SuperAdminRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Header /><NotificationsList /><Footer /></ProtectedRoute>} />
            <Route path="/mitre-navigator" element={<ProtectedRoute><Header /><MITRENavigatorPage /><Footer /></ProtectedRoute>} />
            <Route path="/attack" element={<ProtectedRoute><Header /><AttackPage /><Footer /></ProtectedRoute>} />
            <Route path="/orchestrator" element={<ProtectedRoute><Header /><OrchestratorMonitoring /><Footer /></ProtectedRoute>} />
            <Route path="/llm" element={<ProtectedRoute><Header /><LLMMonitor /><Footer /></ProtectedRoute>} />
            <Route path="/workspaces" element={<ProtectedRoute><Header /><WorkspaceManager /><Footer /></ProtectedRoute>} />
            <Route path="/workspaces/create" element={<Navigate to="/workspaces?action=create" replace />} />
            <Route path="/workspaces/settings" element={<Navigate to="/workspaces" replace />} />
            <Route path="/workspaces/:id" element={<ProtectedRoute><Header /><WorkspaceDetails /><Footer /></ProtectedRoute>} />

            {/* Agents */}
            <Route path="/agents" element={<ProtectedRoute><Header /><AgentsList /><Footer /></ProtectedRoute>} />
            <Route path="/agents/:agentId" element={<ProtectedRoute><Header /><AgentDetails /><Footer /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><Header /><Account /><Footer /></ProtectedRoute>} />
            <Route path="/account/profile" element={<Navigate to="/account?tab=profile" replace />} />
            <Route path="/account/security" element={<Navigate to="/account?tab=security" replace />} />
            <Route path="/account/history" element={<Navigate to="/account?tab=history" replace />} />
            <Route path="/account/contracts" element={<Navigate to="/account?tab=contracts" replace />} />
            <Route path="/account/reports" element={<Navigate to="/account?tab=reports" replace />} />
            <Route path="/account/mobile" element={<Navigate to="/account?tab=mobile" replace />} />
            <Route path="/account/subscription" element={<Navigate to="/account?tab=subscription" replace />} />
            <Route path="/account/invoices" element={<Navigate to="/account?tab=invoices" replace />} />
            <Route path="/account/legal" element={<Navigate to="/account?tab=legal" replace />} />
            <Route path="/account/version" element={<Navigate to="/account?tab=version" replace />} />
            <Route path="/account/support" element={<Navigate to="/account?tab=support" replace />} />
            <Route path="/pricing" element={<ProtectedRoute><Header /><PricingPage /><Footer /></ProtectedRoute>} />
            <Route path="/contact" element={<ProtectedRoute><Header /><ContactPage /><Footer /></ProtectedRoute>} />
            <Route path="/credits" element={<ProtectedRoute><Header /><CreditsPage /><Footer /></ProtectedRoute>} />

            {/* Admin Routes - Super Admin only */}
            <Route path="/admin" element={<SuperAdminRoute><Header /><AdminDashboard /><Footer /></SuperAdminRoute>} />
            <Route path="/admin/companies" element={<SuperAdminRoute><Header /><AdminDashboard /><Footer /></SuperAdminRoute>} />
            <Route path="/admin/users" element={<SuperAdminRoute><Header /><AdminDashboard /><Footer /></SuperAdminRoute>} />
            <Route path="/admin/workspaces" element={<SuperAdminRoute><Header /><AdminDashboard /><Footer /></SuperAdminRoute>} />
            <Route path="/admin/audit-logs" element={<SuperAdminRoute><Header /><AdminDashboard /><Footer /></SuperAdminRoute>} />
            <Route path="/admin/logs" element={<SuperAdminRoute><Header /><AdminDashboard /><Footer /></SuperAdminRoute>} />
            <Route path="/admin/suspicious" element={<SuperAdminRoute><Header /><AdminDashboard /><Footer /></SuperAdminRoute>} />
            <Route path="/admin/settings" element={<SuperAdminRoute><Header /><Settings /><Footer /></SuperAdminRoute>} />

            {/* Support Routes */}
            <Route path="/support/tickets" element={<ProtectedRoute><Header /><TicketsList /><Footer /></ProtectedRoute>} />
            <Route path="/support/tickets/new" element={<ProtectedRoute><Header /><NewTicket /><Footer /></ProtectedRoute>} />
            <Route path="/support/tickets/:id" element={<ProtectedRoute><Header /><TicketDetails /><Footer /></ProtectedRoute>} />

            {/* Contracts - all contract routes redirect to workspaces (contracts are managed within workspace details) */}
            <Route path="/contracts/new" element={<Navigate to="/workspaces" replace />} />
            <Route path="/contracts/:id" element={<Navigate to="/workspaces" replace />} />
            <Route path="/contracts" element={<Navigate to="/workspaces" replace />} />

            {/* License Routes */}
            <Route path="/license-expired" element={<LicenseExpiredPage />} />

            {/* Error Routes */}
            <Route path="/error" element={<ErrorPage />} />
            <Route path="/error/401" element={<ErrorPage />} />
            <Route path="/error/403" element={<ErrorPage />} />
            <Route path="/error/404" element={<ErrorPage />} />
            <Route path="/error/500" element={<ErrorPage />} />

            {/* Catch-all route for 404 */}
            <Route path="*" element={<ErrorPage />} />
          </Routes>
          </ActivityTrackerWrapper>
          </ReportGenerationProvider>
          </WorkspaceProvider>
        </BrowserRouter>
      </div>
    </div>
    </I18nProvider>
  );
}

export default App;
